import { useEffect, useState } from 'react';
import { useRecoilState } from 'recoil';
import {
    fetchDownloadDicomImageBySOP,
    fetchDownloadDicomSeries,
    GetSessionError,
    useGetSession,
} from '~/backend/connector-api/connector-components';
import { Session } from '~/backend/connector-api/connector-schemas';
import useProcessDicomFiles from '~/hooks/useProcessDicomFiles';
import useViewportEnvironment from '~/hooks/useViewportEnvironment';
import { sessionDataAtom } from '~/state/atoms/sessionAtoms';
import { handleError } from '~/utils/errors';

type UseSessionResult = {
    isLoading: boolean;
    error?: Error | GetSessionError | null;
    session: Session | undefined;
};

export default function useSessionData(sessionId: string): UseSessionResult {
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);
    const [sessionData, setSessionData] = useRecoilState(sessionDataAtom);
    const { processImportedFiles } = useProcessDicomFiles();
    const { initializeEnvironment } = useViewportEnvironment();

    const {
        data: sessionDataFromAPI,
        error: sessionError,
        isLoading: isSessionLoading,
    } = useGetSession<Session>({
        pathParams: { sessionID: sessionId },
    });

    const fetchAndProcessFiles = async () => {
        if (
            !sessionData ||
            !sessionData.payload?.context_image ||
            !sessionData.payload?.context_image.series_instance_uid ||
            !sessionData.payload?.context_image.study_instance_uid
        ) {
            return;
        }

        const imageStudyId =
            sessionData.payload.context_image.study_instance_uid;
        const imageSeriesId =
            sessionData.payload.context_image.series_instance_uid;

        setIsLoading(true);

        try {
            const dicomBlob = await fetchDownloadDicomSeries({
                pathParams: { studyId: imageStudyId, seriesId: imageSeriesId },
                queryParams: { series: true },
            });

            const files = [new File([dicomBlob], `${imageSeriesId}.zip`)];

            if (
                sessionData.payload?.context_structure_set?.sop_instance_uid &&
                sessionData.payload?.patient?.id
            ) {
                const structureSetBlob = await fetchDownloadDicomImageBySOP({
                    pathParams: {
                        patientID: sessionData.payload.patient.id,
                        modality: 'RTSTRUCT',
                        sopInstanceUID:
                            sessionData.payload.context_structure_set
                                .sop_instance_uid,
                    },
                });
                files.push(
                    new File(
                        [structureSetBlob],
                        `${sessionData.payload.context_structure_set.sop_instance_uid}.dcm`
                    )
                );
            }

            const processedItems = await processImportedFiles(files);
            if (processedItems) {
                initializeEnvironment(processedItems);
            }
        } catch (fetchError) {
            setError(fetchError as Error);
            handleError(fetchError, "Couldn't process DICOM files. Try again.");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (sessionDataFromAPI) {
            setSessionData(sessionDataFromAPI);
        }
    }, [sessionDataFromAPI]);

    useEffect(() => {
        if (sessionData) {
            fetchAndProcessFiles();
        }
    }, [sessionData]);

    return {
        isLoading: isLoading || isSessionLoading,
        error: error || sessionError,
        session: sessionData,
    };
}
